// @flow

import { useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import * as CookieConsentLib from 'vanilla-cookieconsent';
import {
  updateConsents,
  useSimpalsBar,
} from '@simpals/next-simpalsbar/dist/react';

import { IS_STAGING } from '../../constants';

import { buildCookieConsentConfig } from './buildConfig';
import messages from './messages';
import {
  CONSENT_ANONYMOUS_ID,
  buildUpdateConsentsInput,
  consentSyncFlagFor,
  isForeignConsent,
} from './serverSync';

// The library stylesheet + our theme. Both are global stylesheets.
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './cookieConsent.css';

/**
 * App-wide cookie consent banner (vanilla-cookieconsent v3, self-hosted via npm).
 * Ported from 999-web-app — see ./constants.js for the differences.
 *
 * Renders nothing itself — the library injects its own modal markup into the DOM.
 *
 * simpals-id sync (authenticated users only; write-only — the server record is
 * consent evidence, never read back, so every new device asks again; the
 * cc_consent cookie stays the runtime source of truth; transport/auth via
 * @simpals/next-simpalsbar):
 *  - every real user choice (first consent / preference change) is persisted via
 *    the updateConsents method and stamped with WHO made it (chosenBy: account
 *    id or 'anon') in the consent cookie's data field;
 *  - once per browser session PER ACCOUNT, a device choice not yet attributed to
 *    this account is pushed and adopted via the stamp — that is how a choice made
 *    while logged out ends up on the record of the user who then logs in. A
 *    choice left by a DIFFERENT account is never pushed (that would fabricate
 *    consent evidence); the banner is re-shown so this account chooses for itself.
 *
 * Mounted once, app-wide, from App.js (inside <Intl> so it has the locale).
 */

/**
 * Live consent state of one CMP category, for gating vendor scripts that cannot
 * honour Google Consent Mode themselves.
 *
 * Returns `null` while there is no valid consent yet (first visit, expired or
 * revisioned-out cookie), then a boolean that live-updates on every choice via
 * the library's `cc:onConsent` / `cc:onChange` window events. Treat anything but
 * `true` as "do not load".
 */
export function useConsentCategory(category: string): boolean | null {
  const [granted, setGranted] = useState(null);

  useEffect(() => {
    const update = () => {
      setGranted(
        CookieConsentLib.validConsent()
          ? CookieConsentLib.acceptedCategory(category)
          : null,
      );
    };
    update();
    window.addEventListener('cc:onConsent', update);
    window.addEventListener('cc:onChange', update);
    return () => {
      window.removeEventListener('cc:onConsent', update);
      window.removeEventListener('cc:onChange', update);
    };
  }, [category]);

  return granted;
}

type Props = {
  intl: Object,
};

const CookieConsent = ({ intl }: Props) => {
  const locale = intl.locale;
  const { account } = useSimpalsBar({ isDev: IS_STAGING });

  // Guards against the library being re-initialized for a locale it already
  // runs. Re-runs only when the app locale actually changes.
  const initializedLocale = useRef(null);

  // Resolves once the library has run() — the sync effect must not touch library
  // state (validConsent etc.) before that.
  const runPromiseRef = useRef(null);

  // The library callbacks are created once at run() time; the ref keeps them
  // reading the CURRENT account (SimpalsBar resolves it asynchronously).
  const accountRef = useRef(account);
  accountRef.current = account;

  // Same reason: `intl` is recreated on every locale change, but the callbacks
  // captured at run() time must always format with the current one.
  const intlRef = useRef(intl);
  intlRef.current = intl;

  const translate = (key: string, values?: Object) =>
    intlRef.current.formatMessage(messages[key], values);

  const saveToServer = (acceptedCategories: string[]) => {
    if (!accountRef.current || !accountRef.current.id) return;
    updateConsents(
      buildUpdateConsentsInput({
        acceptedCategories,
        locale,
        t: translate,
      }),
    ).catch(error => {
      // Non-blocking: the cookie already holds the choice; the server record is
      // evidence/bootstrap only. The next change or session retries naturally.
      console.warn(
        '[CookieConsent] failed to persist consent to simpals-id',
        error,
      );
    });
  };

  useEffect(() => {
    if (initializedLocale.current === locale) return;
    initializedLocale.current = locale;

    runPromiseRef.current = CookieConsentLib.run(
      buildCookieConsentConfig({
        locale,
        t: translate,
        // Real user decision: persist it to simpals-id (authenticated users
        // only) and stamp WHO made it into the consent cookie's data field;
        // the reconcile effect below uses that stamp to detect a choice left
        // behind by a different account (which must never be pushed).
        onUserChoice: categories => {
          CookieConsentLib.setCookieData({
            value: {
              chosenBy:
                (accountRef.current && accountRef.current.id) ||
                CONSENT_ANONYMOUS_ID,
            },
            mode: 'update',
          });
          saveToServer(categories);
        },
      }),
    );

    runPromiseRef.current.catch(error => {
      console.warn('[CookieConsent] failed to initialize', error);
    });

    // Consumed by the non-React parts of the page — see public/index.html,
    // where the Bitrix24 form trigger falls back to asking for consent when the
    // (functionality-gated) form loader has not run.
    window.openCookieSettings = () => CookieConsentLib.showPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  // Once per browser session PER ACCOUNT, make sure this account has a server
  // record of the choice this device is running under. Keyed on account.id so
  // switching accounts mid-session covers the new account too (a global flag
  // would silently skip it).
  useEffect(() => {
    const accountId = account && account.id;
    if (!accountId) return;
    if (sessionStorage.getItem(consentSyncFlagFor(accountId))) return;
    if (!runPromiseRef.current) return;

    let cancelled = false;

    runPromiseRef.current
      .then(() => {
        if (cancelled) return;
        sessionStorage.setItem(consentSyncFlagFor(accountId), '1');

        // No valid local choice → the banner is already up and onUserChoice will
        // record whatever the user picks. Nothing to push yet.
        if (!CookieConsentLib.validConsent()) return;

        const cookieData = CookieConsentLib.getCookie('data');
        const chosenBy = cookieData && cookieData.chosenBy;

        // Left behind by ANOTHER account on this shared device: pushing it would
        // fabricate consent evidence for this one. Ask for an own choice instead
        // (the existing local choice keeps governing scripts until then).
        if (isForeignConsent(chosenBy, accountId)) {
          CookieConsentLib.show(true);
          return;
        }

        // Already attributed to this account → it was pushed together with the
        // choice itself, nothing new to send.
        if (chosenBy === accountId) return;

        // Anonymous or legacy unstamped choice made on this device: record it
        // against the account now logging in and adopt the stamp.
        saveToServer(CookieConsentLib.getUserPreferences().acceptedCategories);
        CookieConsentLib.setCookieData({
          value: { chosenBy: accountId },
          mode: 'update',
        });
      })
      .catch(error => {
        // Covers rejection of the init chain itself propagating through this
        // .then, so it is never left unhandled here either.
        console.warn(
          '[CookieConsent] failed to sync consent with simpals-id',
          error,
        );
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account && account.id]);

  return null;
};

export default injectIntl(CookieConsent);
